/** @jsx jsx */
import { Title } from '@bottlebooks/gatsby-design-system';
import { Col, Container, Row } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql as gatsbyGraphQL } from 'gatsby';
import { jsx } from 'theme-ui';
import useLink from '../../useLink';
import BrandAddressSection from '../Brand/BrandAddressSection';
import BrandBanner from '../Brand/BrandBanner';
import BrandDetailsSection from '../Brand/BrandDetailsSection';
import BrandDistributors from '../Brand/BrandDistributors';
import BrandShopsSection from '../Brand/BrandShopsSection';
import ContentRow from '../ContentRow';
import ExhibitorContacts from '../ExhibitorPage/ExhibitorContacts';
import Layout from '../Layout';
import ProducerActions from '../Producer/ProducerActions';
import ProductListListRow from '../ProductListsList/ProductListListRow';
import ProductListsList from '../ProductListsList/ProductListsList';
import ProductCard from '../ProductCard/ProductCard';
import ProductsList from '../ProductsList/ProductsList';
import useSiteConfig from '../useSiteConfig';
import ProducerHeaderRow from './ProducerHeaderRow';

export default function ProducerPage({
  event,
  producer,
  exhibitor,
  products,
  productLists,
  previous,
  next,
  locale,
}) {
  const link = useLink();
  const { showDistributionDetails } = useSiteConfig();
  const hasProducts = products && products.length !== 0;
  const hasProductLists = productLists && productLists.length !== 0;
  const hasProductsSection = hasProducts || hasProductLists;
  return (
    <Layout locale={locale} sx={{ backgroundColor: 'transparent' }}>
      <BrandBanner {...producer} />
      <ProducerHeaderRow
        exhibitor={exhibitor}
        producer={producer}
        previous={previous}
        next={next}
      />

      <Container>
        <Row
          sx={{
            paddingY: 3,
            paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
          }}
        >
          <Col>
            <ProducerActions producer={producer} />
          </Col>
        </Row>

        <ContentRow>
          <Col flex>
            <ExhibitorContacts
              exhibitor={exhibitor}
              eventId={event.eventId}
              sx={{ flex: '1 0 auto' }}
            />
          </Col>
        </ContentRow>

        <Row
          sx={{
            paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
          }}
        >
          <BrandDetailsSection brand={producer} />
        </Row>
        {['all', 'global'].includes(showDistributionDetails) && (
          <ContentRow>
            <Col flex>
              <BrandDistributors brand={producer} />
            </Col>
          </ContentRow>
        )}
      </Container>

      <BrandAddressSection brand={producer} />
      <Container
        sx={{
          paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
        }}
      >
        <BrandShopsSection brand={producer} />
      </Container>

      {hasProductsSection ? (
        <Container fluid sx={{ maxWidth: 'container.fluid' }}>
          <Title sx={{ marginBottom: 4 }}>
            <Trans>Products</Trans>
          </Title>
          {hasProductLists && (
            <ProductListsList sx={{ marginX: [-3, -2], marginY: -2 }}>
              {productLists.map((productList) => (
                <ProductListListRow
                  key={productList.id}
                  productList={productList}
                  to={link.productList(productList)}
                  sx={{ marginX: [0, 2], marginY: [0, 2] }}
                />
              ))}
            </ProductListsList>
          )}

          {hasProducts && (
            <ProductsList
              count={products.length}
              sx={{ marginX: [-3, -2], marginY: -2 }}
            >
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  to={link.product(product)}
                  sx={{ marginBottom: [null, null, 3] }}
                />
              ))}
            </ProductsList>
          )}
        </Container>
      ) : null}
    </Layout>
  );
}

export const fragment = gatsbyGraphQL/* GraphQL */ `
  # Event data for the banner
  fragment ProducerPage_Event on Event {
    name
    ...EventBanner
  }
  # The products (from the Bottlebooks API)
  fragment bb_ProducerPage_Product on Bottlebooks_Product {
    ...bb_ProductListRow
    ...bb_ProductCard
  }
  # The products (some parts from the pubCache)
  fragment ProducerPage_Product on Product {
    id
    ...ProductListRow
    ...ProductCard
  }
  # For producer pages showing product lists too we query on the event type (for now).
  # This used mainly for single producer directories.
  fragment ProducerPage_ProductList on Event {
    id
    ...ProductListListRow
    ...useLink_ProductList
  }

  fragment ProducerPage on Producer {
    ...BrandBanner
    ...ProducerHeaderRow
    products {
      ...ProducerPage_Product
      ...useLink_Product
    }
  }

  fragment bb_Registration_ProducerPage on Bottlebooks_Registration {
    ...bb_Registration
    ...bb_Registration_ProducerHeaderRow
  }

  fragment bb_RegisteredBrand_ProducerPage on Bottlebooks_RegisteredBrand {
    ...bb_RegisteredBrand_ProducerHeaderRow
  }

  fragment bb_ProducerPage on Bottlebooks_Producer {
    name
    ...bb_BrandDetailsSection
    ...bb_BrandDistributors
    ...bb_BrandAddressSection
    ...bb_BrandShopsSection
    products {
      nodes {
        ...bb_ProductListRow
        ...bb_ProductCard
      }
    }
  }
`;
